/**
 * Mobile OAuth Handler Utilities
 * Handles Instagram OAuth flow for mobile browsers to prevent native app intercept
 */

interface OAuthCallbackData {
  token: string;
  timestamp: number;
}

/**
 * Detects if the current device is a mobile browser (iOS Safari, Android Chrome, etc.)
 * @returns True if mobile device, false if desktop
 */
export const isMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as Window & { opera?: string }).opera;
  return /android|iPad|iPhone|iPod/i.test(userAgent ?? '') && !((window as Window & { MSStream?: unknown }).MSStream);
};

/**
 * Opens OAuth URL in a popup window to prevent Instagram app intercept on mobile
 * CRITICAL: This must be called synchronously within a user gesture handler (iOS requirement)
 *
 * @param authUrl - The Instagram OAuth authorization URL
 * @returns The popup window object, or null if blocked
 */
export const openOAuthPopup = (authUrl: string): Window | null => {
  const width = 500;
  const height = 700;
  const left = (screen.width - width) / 2;
  const top = (screen.height - height) / 2;

  // CRITICAL: Must open synchronously (iOS blocks async popups)
  const popup = window.open(
    authUrl,
    'InstagramOAuth',
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
  );

  return popup;
};

/**
 * Monitors localStorage for OAuth callback completion
 * This approach bypasses CORS/Same-Origin restrictions on mobile browsers
 *
 * Instead of trying to read popup.location.href (blocked by iOS Safari),
 * the popup redirects to /oauth-callback which stores the token in localStorage,
 * and this function polls localStorage to detect it.
 *
 * @param popup - The popup window object to monitor
 * @param onSuccess - Callback when token is received: (token) => void
 * @param onError - Callback when error occurs: (error) => void
 * @returns The interval ID (for cleanup if needed)
 */
export const monitorPopup = (popup: Window, onSuccess: (token: string) => void, onError: (error: Error) => void): ReturnType<typeof setInterval> => {
  // Clear any previous OAuth callback data
  localStorage.removeItem('instagram_oauth_callback');
  localStorage.removeItem('instagram_oauth_error');

  const lastCheckedTimestamp = Date.now();

  const pollInterval = setInterval(() => {
    try {
      // Check if user closed popup
      if (popup.closed) {
        clearInterval(pollInterval);

        // Check if we got a callback before popup closed
        const callbackData = localStorage.getItem('instagram_oauth_callback');
        const errorData = localStorage.getItem('instagram_oauth_error');

        if (callbackData) {
          try {
            const { token, timestamp } = JSON.parse(callbackData) as OAuthCallbackData;

            // Verify this is a new callback (not stale data)
            if (timestamp > lastCheckedTimestamp) {
              console.log('OAuth callback detected in localStorage');
              localStorage.removeItem('instagram_oauth_callback');
              onSuccess(token);
              return;
            }
          } catch (parseError) {
            console.error('Error parsing callback data:', parseError);
          }
        }

        if (errorData) {
          console.error('OAuth error detected:', errorData);
          localStorage.removeItem('instagram_oauth_error');
          onError(new Error(errorData));
          return;
        }

        // Popup closed without callback - user cancelled
        onError(new Error('OAuth window was closed by user'));
        return;
      }

      // Check for callback while popup is still open
      const callbackData = localStorage.getItem('instagram_oauth_callback');
      if (callbackData) {
        try {
          const { token, timestamp } = JSON.parse(callbackData) as OAuthCallbackData;

          // Verify this is a new callback
          if (timestamp > lastCheckedTimestamp) {
            clearInterval(pollInterval);
            localStorage.removeItem('instagram_oauth_callback');

            // Close popup if still open
            if (popup && !popup.closed) {
              popup.close();
            }

            console.log('OAuth callback successful');
            onSuccess(token);
            return;
          }
        } catch (parseError) {
          console.error('Error parsing callback data:', parseError);
        }
      }

      // Check for error
      const errorData = localStorage.getItem('instagram_oauth_error');
      if (errorData) {
        clearInterval(pollInterval);
        localStorage.removeItem('instagram_oauth_error');

        // Close popup if still open
        if (popup && !popup.closed) {
          popup.close();
        }

        onError(new Error(errorData));
        return;
      }

    } catch (e) {
      console.error('Error in popup monitor:', e);
    }
  }, 300); // Poll every 300ms for faster response

  // Timeout after 5 minutes
  setTimeout(() => {
    clearInterval(pollInterval);
    if (popup && !popup.closed) {
      popup.close();
    }

    // Check one last time for callback
    const callbackData = localStorage.getItem('instagram_oauth_callback');
    if (callbackData) {
      try {
        const { token } = JSON.parse(callbackData) as OAuthCallbackData;
        localStorage.removeItem('instagram_oauth_callback');
        onSuccess(token);
        return;
      } catch {
        // Fallthrough to timeout error
      }
    }

    localStorage.removeItem('instagram_oauth_callback');
    localStorage.removeItem('instagram_oauth_error');
    onError(new Error('OAuth timeout - window was open for more than 5 minutes'));
  }, 300000);

  return pollInterval;
};

/**
 * Extracts Instagram token from URL parameters
 * @param url - The URL to parse
 * @returns The token if found, null otherwise
 */
export const extractTokenFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('instagram_token');
  } catch (error) {
    console.error('Error parsing URL for token:', error);
    return null;
  }
};

/**
 * Extracts Instagram error from URL parameters
 * @param url - The URL to parse
 * @returns The error if found, null otherwise
 */
export const extractErrorFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('instagram_error');
  } catch (error) {
    console.error('Error parsing URL for error:', error);
    return null;
  }
};
